import React from 'react'
import { cloneDeep } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { isBefore } from 'date-fns'
import { AddCircle, ArrowDown2 } from 'iconsax-react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import BaseModal from '../BaseModal2'
import Section from './Section'
import ModalTextField from '../../../atoms/ModalTextField'
import Checkbox from '../../../../pages/Settings/Controls/Checkbox'
import EndControls from '../EndControls'
import ColorInput from './ColorInput'
import Dropdown from '../../../atoms/Dropdown'
import ErrorToast from '../../../molecules/ErrorToast'
import Tooltip from '../../../atoms/Tooltip'
import UserSearchDropdown from '../../UserSearchDropdown'
import PhoneInput from '../../../atoms/PhoneInput'
import QuestionSection from './QuestionSection'
import ContentSection from './ContentSection'
import Button from '../../../atoms/Button'

import {
  INITIAL_VALUES,
  formatDataFromAPI,
  getCategory,
  getType,
  formatPayload,
  TIMEOUT_OPTIONS,
  SECURITY_OPTIONS,
  LOCATION_OPTIONS,
  getInbox
} from './helpers'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { selectCategory, fetchCategories } from '@SonarCX/slices/category'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useIntegrations } from '@SonarCX/providers/IntegrationProvider'
import { fetchTypes } from '@SonarCX/slices/taskType'
import { fetchInboxes } from '@SonarCX/slices/inboxes'
import { checkE164 } from '@SonarCX/utilities'
import { AddSuccessAlert } from '@SonarCX/slices/alert'

import useStyles from './styles'

const SMS = 'sms'
const VOICE = 'voice'

const IntegrationModal = ({ open, onClose, data }) => {
  const dispatch = useAppDispatch()
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const { allUsers } = useUserAPI()
  const { postInt, putInt, setError, error } = useIntegrations()
  const taskTypeSelector = useAppSelector(storeState => storeState.taskType)
  const categorySelector = useAppSelector(selectCategory)
  const inboxesSelector = useAppSelector(storeState => storeState.inboxes)
  const { typesValues: types, loaded: loadedTypes } = taskTypeSelector
  const { categoriesValues: categories, loaded: loadedCategories } =
    categorySelector
  const { inboxesValues: inboxes } = inboxesSelector
  const [values, setValues] = React.useState(null)
  const [editing, setEditing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [generate, setGenerate] = React.useState(false)
  const [enabled, setEnabled] = React.useState(false)
  const [withKB, setWithKB] = React.useState(false)
  const [disableLiveChat, setDisableLiveChat] = React.useState(false)
  const [changed, setChanged] = React.useState(false)
  const [smsError, setSmsError] = React.useState(null)
  const [voiceError, setVoiceError] = React.useState(null)

  // This form is super re-render heavy, we have many states that update the entire form, using refs for messages section is a workaround to prevent re rendering
  //new answer input ref
  const newAnserRef = React.createRef(null)

  //default messages refs
  const kb_titleRef = React.createRef(null)
  const kb_introRef = React.createRef(null)
  const kb_send_messageRef = React.createRef(null)
  const kb_article_nameRef = React.createRef(null)
  const kb_article_name_pluralRef = React.createRef(null)
  const online_introRef = React.createRef(null)
  const offline_introRef = React.createRef(null)
  const offline_intro_endRef = React.createRef(null)
  const m_offline_introRef = React.createRef(null)
  const m_offline_intro_endRef = React.createRef(null)
  const offline_submitRef = React.createRef(null)

  // Only 3 hours of operation can be added
  const addHoursDisabled = React.useMemo(() => {
    if (!values) {
      return false
    }
    const hours = values.widget_config.hours_of_operation
    return Object.keys(hours).length >= 3
  }, [values])

  const classes = useStyles({ addHoursDisabled })

  const checkValidation = (key, val) => {
    if (key === SMS) {
      const smsNumber = val
      if (smsNumber === '' || smsNumber === null) return

      const error = checkE164(smsNumber)[0]
      if (error) {
        setSmsError(error)
      }
    } else {
      const voiceNumber = val
      if (voiceNumber === '' || voiceNumber === null) return

      const error = checkE164(voiceNumber)[0]
      if (error) {
        setVoiceError(error)
      }
    }
  }

  React.useEffect(() => {
    if (!taskTypeSelector.loaded && !taskTypeSelector.loading) {
      dispatch(fetchTypes())
    }
  }, [taskTypeSelector, dispatch])

  React.useEffect(() => {
    if (!categorySelector.loaded && !categorySelector.loading) {
      dispatch(fetchCategories())
    }
  }, [categorySelector, dispatch])

  React.useEffect(() => {
    if (!inboxesSelector.loaded && !inboxesSelector.loading) {
      dispatch(fetchInboxes())
    }
  }, [inboxesSelector, dispatch])

  // Updates local values if editing
  React.useEffect(() => {
    setEditing(Boolean(data))
    setValues(data ? formatDataFromAPI(data) : INITIAL_VALUES)
    setEnabled(Boolean(data?.widget_config))
    setWithKB(Boolean(data?.widget_config?.with_kb))
    setDisableLiveChat(Boolean(data?.widget_config?.disable_live_chat))
  }, [data])

  // Helper method to set error
  const showError = React.useCallback(
    message => {
      setError({ err: { message } })
    },
    [setError]
  )

  const handleQuestionChange = (idx, prop, value) => {
    if (prop && value.trim() !== '') {
      setChanged(true)
      setValues(old => {
        const questions = [...old.widget_config.questions]
        questions[idx] = {
          ...questions[idx],
          [prop]: value
        }

        return {
          ...old,
          widget_config: {
            ...old.widget_config,
            questions
          }
        }
      })
    }
  }

  const handleAnswerAdd = idx => {
    const value = newAnserRef.current?.value || ''
    if (value.trim() !== '') {
      const questionsCopy = cloneDeep(values.widget_config.questions)
      const currentOptions = questionsCopy[idx]?.options
      if (currentOptions.some(opts => opts.value === value)) return

      setChanged(true)
      questionsCopy[idx].options.push({ value, inboxes: [] })
      setValues(old => ({
        ...old,
        widget_config: {
          ...old.widget_config,
          questions: [...questionsCopy]
        }
      }))
      if (newAnserRef.current) {
        newAnserRef.current.value = ''
      }
    }
  }

  const handleAnswerInboxAdd = (idx, value, inbox) => {
    const questionsCopy = cloneDeep(values.widget_config.questions)
    const currentOptions = questionsCopy[idx]?.options
    const currentAnswer = currentOptions.find(opts => opts.value === value)
    if (currentAnswer.inboxes.some(ibx => ibx === inbox)) return

    setChanged(true)
    const result = questionsCopy.map((question, index) => {
      if (index === idx) {
        question.options.find(opt => opt.value === value).inboxes = [
          ...currentAnswer.inboxes,
          inbox
        ]
      }
      return question
    })
    setValues(old => ({
      ...old,
      widget_config: {
        ...old.widget_config,
        questions: [...result]
      }
    }))
  }

  const handleRemoveQuestion = index => {
    setChanged(true)
    const questionsCopy = [
      ...values.widget_config.questions.filter((x, idx) => idx !== index)
    ]

    setValues(old => ({
      ...old,
      widget_config: {
        ...old.widget_config,
        questions: [...questionsCopy]
      }
    }))
  }
  // Updates stored values
  const handleChange = React.useCallback(
    key => e => {
      setChanged(true)
      if (key.startsWith('theme.')) {
        const prop = key.split('theme.')[1]
        setValues(old => ({
          ...old,
          widget_config: {
            ...old.widget_config,
            theme: {
              ...old.widget_config.theme,
              [prop]: e
            }
          }
        }))
      } else if (Object.keys(INITIAL_VALUES.widget_config).includes(key)) {
        setValues(old => ({
          ...old,
          widget_config: {
            ...old.widget_config,
            [key]: e
          }
        }))
      } else if (key.startsWith('task_config.')) {
        const prop = key.split('task_config.')[1]
        setValues(old => ({
          ...old,
          widget_config: {
            ...old.widget_config,
            task_config: {
              ...old.widget_config.task_config,
              [prop]: e
            }
          }
        }))
      } else if (key.startsWith('messages.')) {
        const prop = key.split('messages.')[1]
        setValues(old => ({
          ...old,
          widget_config: {
            ...old.widget_config,
            messages: {
              ...old.widget_config.messages,
              [prop]: e
            }
          }
        }))
      } else {
        const target = e.target
        const val = target.value
        setValues(old => ({ ...old, [key]: val }))
      }
    },
    []
  )

  // Adds topics
  const handleAdd = React.useCallback(
    (type, val) => {
      setChanged(true)
      if (type === 'question') {
        setValues(old => {
          const newQuestions = [
            ...old.widget_config.questions,
            ...[
              {
                title: '',
                options: []
              }
            ]
          ]

          return {
            ...old,
            widget_config: {
              ...old.widget_config,
              questions: newQuestions
            }
          }
        })
      } else if (type === 'hours') {
        const { newStart, newEnd, newDays: days } = val
        if (!days.length) {
          return showError('Please select at least one day')
        }
        const start = newStart._d ?? newStart
        const end = newEnd._d ?? newEnd
        if (isBefore(end, start)) {
          return showError('End time must come after start time')
        }
        setValues(old => ({
          ...old,
          widget_config: {
            ...old.widget_config,
            hours_of_operation: {
              ...old.widget_config.hours_of_operation,
              [uuidv4()]: { start, end, days }
            }
          }
        }))
      }
    },
    [showError]
  )

  const handleMoveAnswer = React.useCallback((start, end, questionIndex) => {
    if (start !== end) {
      setChanged(true)
      setValues(old => {
        const oldAnswers = cloneDeep(
          old.widget_config.questions[questionIndex]?.options
        )

        // Calculate indexes
        const oldIdx = oldAnswers.findIndex(v => v.value === start)
        const newIdx = oldAnswers.findIndex(v => v.value === end)

        const [removed] = oldAnswers.splice(oldIdx, 1)
        oldAnswers.splice(newIdx, 0, removed)

        const updatedQuestions = cloneDeep(old.widget_config.questions).map(
          (q, index) => {
            if (index === questionIndex) {
              q.options = [...oldAnswers]
            }
            return q
          }
        )

        return {
          ...old,
          widget_config: {
            ...old.widget_config,
            questions: [...updatedQuestions]
          }
        }
      })
    }
  }, [])

  const handleMoveAnswerInbox = React.useCallback(
    (start, end, answerValue, questionIndex) => {
      if (start !== end) {
        setChanged(true)
        setValues(old => {
          const oldAnswers = cloneDeep(
            old.widget_config.questions[questionIndex]?.options
          )
          const oldAnswersInboxes = [
            ...oldAnswers.find(x => x.value === answerValue).inboxes
          ]

          const oldIdx = oldAnswersInboxes.findIndex(v => v === start)
          const newIdx = oldAnswersInboxes.findIndex(v => v === end)

          const [removed] = oldAnswersInboxes.splice(oldIdx, 1)
          oldAnswersInboxes.splice(newIdx, 0, removed)

          const updatedQuestions = cloneDeep(old.widget_config.questions).map(
            (q, index) => {
              if (index === questionIndex) {
                q.options.find(opt => opt.value === answerValue).inboxes = [
                  ...oldAnswersInboxes
                ]
              }
              return q
            }
          )

          return {
            ...old,
            widget_config: {
              ...old.widget_config,
              questions: [...updatedQuestions]
            }
          }
        })
      }
    },
    []
  )

  const handleRemoveAnswer = React.useCallback((value, questionIndex) => {
    setChanged(true)
    setValues(old => {
      const updatedQuestions = cloneDeep(old.widget_config.questions).map(
        (q, index) => {
          if (index === questionIndex) {
            const newOptions = q.options.filter(opts => opts.value !== value)
            q.options = newOptions
          }
          return q
        }
      )

      return {
        ...old,
        widget_config: {
          ...old.widget_config,
          questions: [...updatedQuestions]
        }
      }
    })
  }, [])

  const handleRemoveAnswerInbox = React.useCallback(
    (answerValue, value, questionIndex) => {
      setChanged(true)
      setValues(old => {
        const oldAnswers = cloneDeep(
          old.widget_config.questions[questionIndex]?.options
        )
        const oldAnswersInboxes = [
          ...oldAnswers
            .find(x => x.value === answerValue)
            .inboxes.filter(opts => opts !== value)
        ]

        const updatedQuestions = cloneDeep(old.widget_config.questions).map(
          (q, index) => {
            if (index === questionIndex) {
              q.options.find(opt => opt.value === answerValue).inboxes = [
                ...oldAnswersInboxes
              ]
            }
            return q
          }
        )

        return {
          ...old,
          widget_config: {
            ...old.widget_config,
            questions: [...updatedQuestions]
          }
        }
      })
    },
    []
  )

  // Resets modal values and closes
  const handleClose = React.useCallback(() => {
    setValues(INITIAL_VALUES)
    setEditing(false)
    setGenerate(false)
    setEnabled(false)
    setError(null)
    setSmsError('')
    setVoiceError('')
    setChanged(false)
    setSaving(false)
    onClose()
  }, [onClose, setError])

  const getMessagesValues = React.useCallback(() => {
    return {
      kb_title: kb_titleRef.current?.value,
      kb_intro: kb_introRef.current?.value,
      kb_send_message: kb_send_messageRef.current?.value,
      kb_article_name: kb_article_nameRef.current?.value,
      kb_article_name_plural: kb_article_name_pluralRef.current?.value,
      online_intro: online_introRef.current?.value,
      offline_intro: offline_introRef.current?.value,
      offline_intro_end: offline_intro_endRef.current?.value,
      m_offline_intro: m_offline_introRef.current?.value,
      m_offline_intro_end: m_offline_intro_endRef.current?.value,
      offline_submit: offline_submitRef.current?.value
    }
  }, [
    kb_titleRef,
    kb_introRef,
    kb_send_messageRef,
    kb_article_nameRef,
    kb_article_name_pluralRef,
    online_introRef,
    offline_introRef,
    offline_intro_endRef,
    m_offline_introRef,
    m_offline_intro_endRef,
    offline_submitRef
  ])

  // Handles submitting the changes values to create/edit the integration
  const handleSubmit = React.useCallback(() => {
    const { data, error } = formatPayload(
      values,
      getMessagesValues(),
      enabled,
      withKB,
      disableLiveChat
    )
    if (error) {
      return showError(error)
    }

    if (!changed) {
      handleClose()
      return
    }
    if (editing) {
      setTitle('ARE YOU SURE?')
      setReason('Updating settings will take place immediately.')
      setSubtext('Everything using this integration will be affected.')
      setV2(true)
      setAction(() => {
        try {
          setSaving(true)
          putInt(values.id, data).then(int => {
            dispatch(AddSuccessAlert('Changes saved'))
            int && setSaving(false)
            handleClose()
          })
        } catch (error) {
          showError(error)
        }
      })
      return
    }

    postInt(data)
      .then(int => {
        int && setSaving(false)
        dispatch(AddSuccessAlert('Integration created'))
        handleClose()
      })
      .catch(error => {
        showError(error)
      })
  }, [
    values,
    enabled,
    editing,
    showError,
    handleClose,
    setReason,
    setSubtext,
    setAction,
    setTitle,
    setV2,
    putInt,
    postInt,
    changed,
    withKB,
    disableLiveChat,
    getMessagesValues,
    dispatch
  ])

  if (![open, values, loadedTypes, loadedCategories].every(Boolean)) {
    return null
  }

  return (
    <>
      <BaseModal
        open={open}
        showTitleOnMobile={true}
        onClose={() => {
          const action = handleClose

          if (changed) {
            setReason('You have unsaved changes')
            setAction(action)
            setV2(true)
          } else {
            action()
          }
        }}
        title={`${editing ? 'Edit' : 'Create'} Integration`}
        PaperProps={{ style: { width: '50%' } }}
      >
        <ModalTextField title='Name' className={classes.input}>
          <input
            placeholder='Integration name'
            defaultValue={values.name}
            onBlur={handleChange('name')}
          />
        </ModalTextField>

        {!editing && (
          <Checkbox
            checked={generate}
            onCheck={val => {
              setGenerate(val)
              setChanged(true)
            }}
          >
            Generate API key
          </Checkbox>
        )}

        <Checkbox
          checked={enabled}
          onCheck={val => {
            const action = () => {
              setEnabled(val)
              setChanged(true)
            }

            if (enabled) {
              setTitle('ARE YOU SURE?')

              setReason('Are you sure you want to disable this widget?')
              setSubtext('Doing so will reset any configuration.')
              setAction(action)
              setV2(true)
            } else {
              action()
            }
          }}
        >
          Enable widget
        </Checkbox>

        {enabled && (
          <>
            <Section name='Theme'>
              <ColorInput
                name='Start Color'
                saveColor={handleChange('theme.start_color')}
                prevColor={values.widget_config.theme.start_color}
              />

              <ColorInput
                name='End Color'
                saveColor={handleChange('theme.end_color')}
                prevColor={values.widget_config.theme.end_color}
                className={classes.color}
              />

              <div className={classes.dropdown}>
                <div>Location</div>

                <Dropdown
                  options={Object.keys(LOCATION_OPTIONS)}
                  renderOption={key => LOCATION_OPTIONS[key]}
                  selected={values.widget_config.theme.location}
                  onSelect={handleChange('theme.location')}
                  hideAdd
                  hideClose
                >
                  <div className='menu'>
                    <div>
                      {LOCATION_OPTIONS[values.widget_config.theme.location]}
                    </div>

                    <ArrowDown2 />
                  </div>
                </Dropdown>
              </div>
            </Section>

            <Section name='Contact Numbers'>
              <div className={classes.contactNumbers}>
                <div>
                  <span>
                    <label htmlFor='sms-number'>SMS number</label>
                    <PhoneInput
                      placeholder='SMS number'
                      name='sms-number'
                      value={values.widget_config.sms_number}
                      isValid={!Boolean(smsError)}
                      onBlur={e => {
                        const val = e.target.value
                        handleChange('sms_number')(val.trim())
                        checkValidation(SMS, val)
                      }}
                      onFocus={() => {
                        setSmsError(null)
                      }}
                    />
                  </span>
                  {smsError && <small>{smsError}</small>}
                </div>
                <div>
                  <span>
                    <label htmlFor='voice-number'>Voice number</label>
                    <PhoneInput
                      placeholder='Voice number'
                      name='voice-number'
                      value={values.widget_config.voice_number}
                      isValid={!Boolean(voiceError)}
                      onBlur={e => {
                        const val = e.target.value
                        handleChange('voice_number')(val.trim())
                        checkValidation(VOICE, val)
                      }}
                      onFocus={() => {
                        setVoiceError(null)
                      }}
                    />
                  </span>
                  {voiceError && <small>{voiceError}</small>}
                </div>
              </div>
            </Section>

            <Section name='Knowledge Base'>
              <Checkbox
                checked={withKB}
                onCheck={val => {
                  setWithKB(val)
                  setChanged(true)
                }}
              >
                Include knowledge base
              </Checkbox>
            </Section>

            <Section name='Live Chat'>
              <Checkbox
                checked={disableLiveChat}
                onCheck={val => {
                  setDisableLiveChat(val)
                  setChanged(true)
                }}
              >
                Disable live chat
              </Checkbox>
            </Section>

            <Section name='Pre-Chat Survey'>
              {/* only allow 1 question FOR NOW */}
              {values.widget_config.questions.map((question, idx) => {
                return (
                  <React.Fragment key={idx}>
                    <QuestionSection
                      question={question}
                      idx={idx}
                      handleAdd={handleAnswerAdd}
                      handleQuestionChange={handleQuestionChange}
                      handleMoveAnswer={handleMoveAnswer}
                      handleRemoveAnswer={handleRemoveAnswer}
                      inboxes={inboxes}
                      handleAnswerInboxAdd={handleAnswerInboxAdd}
                      handleMoveAnswerInbox={handleMoveAnswerInbox}
                      handleRemoveAnswerInbox={handleRemoveAnswerInbox}
                      newAnserRef={newAnserRef}
                    />
                    <span>
                      <Button
                        invert='true'
                        className={classes.deleteButton}
                        onClick={() => {
                          handleRemoveQuestion(idx)
                        }}
                      >
                        Remove Question
                      </Button>
                    </span>
                  </React.Fragment>
                )
              })}

              {values.widget_config.questions.length === 0 && (
                <div
                  onClick={() => handleAdd('question', {})}
                  className={classes.addHours}
                >
                  <AddCircle className='button' />
                  Add Question
                </div>
              )}
            </Section>

            <Section name='Inbox'>
              <div className={classes.dropdown}>
                <div>Default Inbox</div>

                <Dropdown
                  options={inboxes.map(t => t.id)}
                  renderOption={key => getInbox(inboxes, key)}
                  selected={values.widget_config.inbox_id}
                  onSelect={handleChange('inbox_id')}
                  hideAdd
                  hideClose
                  stringOnly
                  disablePortal
                >
                  <div className='menu'>
                    <div>
                      {getInbox(inboxes, values.widget_config.inbox_id)}
                    </div>

                    <ArrowDown2 />
                  </div>
                </Dropdown>
              </div>

              {!values.widget_config.inbox_id && (
                <div className={classes.warning}>Inbox must be selected</div>
              )}
            </Section>

            <Section name='Security'>
              <div className={classes.dropdown}>
                <div>Schema</div>

                <Dropdown
                  options={Object.keys(SECURITY_OPTIONS)}
                  renderOption={key => SECURITY_OPTIONS[key]}
                  selected={values.widget_config.security}
                  onSelect={handleChange('security')}
                  hideAdd
                  hideClose
                >
                  <div className='menu'>
                    <div>{SECURITY_OPTIONS[values.widget_config.security]}</div>

                    <ArrowDown2 />
                  </div>
                </Dropdown>
              </div>

              <div className={classes.dropdown}>
                <div>
                  <Tooltip
                    title='This is the length of time from no interaction with the user
                  before we timeout their session and reset the widget'
                    arrow
                  >
                    <div>Idle Timeout</div>
                  </Tooltip>
                </div>

                <Dropdown
                  options={Object.keys(TIMEOUT_OPTIONS)}
                  renderOption={key => TIMEOUT_OPTIONS[key]}
                  selected={values.widget_config.timeout}
                  onSelect={handleChange('timeout')}
                  hideAdd
                  hideClose
                  stringOnly
                >
                  <div className='menu'>
                    <div>{TIMEOUT_OPTIONS[values.widget_config.timeout]}</div>

                    <ArrowDown2 />
                  </div>
                </Dropdown>
              </div>
            </Section>

            <Section name='Task Options'>
              <div className={classes.dropdown}>
                <div>Category</div>

                <Dropdown
                  options={categories.map(c => c.id)}
                  renderOption={key => getCategory(categories, key)}
                  selected={values.widget_config.task_config.category_id}
                  onSelect={handleChange('task_config.category_id')}
                  hideAdd
                  hideClose
                  stringOnly
                  disablePortal
                >
                  <div className='menu'>
                    <div>
                      {getCategory(
                        categories,
                        values.widget_config.task_config.category_id
                      )}
                    </div>

                    <ArrowDown2 />
                  </div>
                </Dropdown>
              </div>

              <div className={classes.dropdown}>
                <div>Type</div>

                <Dropdown
                  options={types.map(t => t.id)}
                  renderOption={key => getType(types, key)}
                  selected={values.widget_config.task_config.type_id}
                  onSelect={handleChange('task_config.type_id')}
                  hideAdd
                  hideClose
                  stringOnly
                  disablePortal
                >
                  <div className='menu'>
                    <div>
                      {getType(types, values.widget_config.task_config.type_id)}
                    </div>

                    <ArrowDown2 />
                  </div>
                </Dropdown>
              </div>

              <div className={classes.dropdown}>
                <div>Assignee</div>

                <UserSearchDropdown
                  prevSelected={
                    values.widget_config.task_config.assignee_id
                      ? [values.widget_config.task_config.assignee_id]
                      : []
                  }
                  selected={values.widget_config.task_config.assignee_id}
                  onSelect={handleChange('task_config.assignee_id')}
                  limit={10}
                  disablePortal
                >
                  <div className='menu'>
                    <div>
                      {allUsers[values.widget_config.task_config.assignee_id]
                        ?.name ?? 'Unassigned'}
                    </div>

                    <ArrowDown2 />
                  </div>
                </UserSearchDropdown>
              </div>
            </Section>

            <Section name='Content'>
              <ContentSection
                values={values}
                kb_titleRef={kb_titleRef}
                kb_introRef={kb_introRef}
                kb_send_messageRef={kb_send_messageRef}
                kb_article_nameRef={kb_article_nameRef}
                kb_article_name_pluralRef={kb_article_name_pluralRef}
                online_introRef={online_introRef}
                offline_introRef={offline_introRef}
                offline_intro_endRef={offline_intro_endRef}
                m_offline_introRef={m_offline_introRef}
                m_offline_intro_endRef={m_offline_intro_endRef}
                offline_submitRef={offline_submitRef}
                setChanged={setChanged}
              />
            </Section>
          </>
        )}

        <EndControls
          disabled={Boolean(smsError || voiceError)}
          onSubmit={handleSubmit}
          submitText='SAVE'
          showExtraIcons={false}
          SubmitIcon={() => null}
          saving={saving}
        />
      </BaseModal>

      <ErrorToast error={error} autoHideDuration={2000} />
    </>
  )
}

export default IntegrationModal
